import { useEffect, useState } from "react";
import api, { extractErr } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Layers, Loader2, Save, Search, LockOpen, Check } from "lucide-react";

const inr2 = (n) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function SuperBulkAddons() {
  const [bundle, setBundle] = useState(null);
  const [price, setPrice] = useState("");
  const [saving, setSaving] = useState(false);
  const [purchasers, setPurchasers] = useState([]);
  const [q, setQ] = useState("");
  const [hits, setHits] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState({});
  const [granting, setGranting] = useState(false);

  const load = async () => {
    try {
      const [{ data: b }, { data: p }] = await Promise.all([
        api.get("/platform/addons/bundle"),
        api.get("/platform/addons/bundle/purchasers"),
      ]);
      setBundle(b);
      setPrice(String(b.price ?? ""));
      setPurchasers(p.items || []);
    } catch (e) {
      toast.error(extractErr(e));
    }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const needle = q.trim();
    if (needle.length < 2) {
      setHits([]);
      setSearching(false);
      return;
    }
    let alive = true;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/platform/addons/bundle/tenants", { params: { q: needle } });
        if (alive) setHits(data.items || []);
      } catch (e) {
        if (alive) toast.error(extractErr(e));
      } finally {
        if (alive) setSearching(false);
      }
    }, 280);
    return () => { alive = false; clearTimeout(t); };
  }, [q]);

  const savePrice = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/platform/addons/bundle", { price: Number(price) });
      setBundle(data);
      setPrice(String(data.price ?? ""));
      toast.success("Bundle price saved");
    } catch (e) {
      toast.error(extractErr(e));
    } finally {
      setSaving(false);
    }
  };

  const toggle = (t) => {
    setSelected((s) => {
      const next = { ...s };
      if (next[t.id]) delete next[t.id];
      else next[t.id] = t;
      return next;
    });
  };

  const grant = async () => {
    const ids = Object.keys(selected);
    if (!ids.length) {
      toast.error("Select at least one tenant");
      return;
    }
    setGranting(true);
    try {
      const { data } = await api.put("/platform/addons/bundle/grant", { tenant_ids: ids, days: 365 });
      toast.success(`Unlocked all addons for ${data.granted?.length || ids.length} store(s)`);
      setSelected({});
      setQ("");
      setHits([]);
      await load();
    } catch (e) {
      toast.error(extractErr(e));
    } finally {
      setGranting(false);
    }
  };

  const picked = Object.values(selected);

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="font-display text-3xl font-bold text-slate-100 flex items-center gap-2">
          <Layers className="w-7 h-7 text-fuchsia-300" /> Bulk addons
        </h1>
        <p className="text-slate-400 text-sm mt-1">Set the App Store “buy all addons” price, see who bought the bundle, and grant every addon for 1 year.</p>
      </div>

      <Card className="bg-slate-900 border-slate-800 p-6 space-y-4 text-slate-100" data-testid="bulk-price-card">
        <div className="font-semibold">Buy-all price (₹ / year, before GST)</div>
        {bundle && (
          <p className="text-xs text-slate-400">
            Individual catalog total ₹{inr2(bundle.list_price)} · payable with GST ₹{inr2(bundle.total)} · {bundle.addon_count} apps
            {bundle.addon_names?.length ? ` (${bundle.addon_names.join(", ")})` : ""}
          </p>
        )}
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[160px]">
            <Label className="text-slate-300">Amount</Label>
            <Input type="number" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} className="mt-1 bg-slate-950 border-slate-700 font-mono-nums" data-testid="bulk-price-input" />
          </div>
          <Button onClick={savePrice} disabled={saving} className="bg-indigo-600 hover:bg-indigo-500" data-testid="bulk-price-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />} Save price
          </Button>
        </div>
      </Card>

      <Card className="bg-slate-900 border-slate-800 p-6 space-y-4 text-slate-100" data-testid="bulk-grant-card">
        <div className="font-semibold">Give 1-year access to all addons</div>
        <div className="relative">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search tenant name, slug, or email…" className="pl-9 bg-slate-950 border-slate-700" data-testid="bulk-tenant-search" />
        </div>
        {searching && <div className="text-xs text-slate-500 flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Searching…</div>}
        {hits.length > 0 && (
          <div className="space-y-2">
            {hits.map((t) => {
              const on = Boolean(selected[t.id]);
              const active = Boolean(t.entitlement?.active);
              return (
                <button
                  type="button"
                  key={t.id}
                  onClick={() => toggle(t)}
                  className={`w-full text-left flex items-center justify-between gap-2 rounded-lg border px-3 py-2 ${on ? "border-fuchsia-500 bg-fuchsia-500/10" : "border-slate-800 bg-slate-950"}`}
                  data-testid={`bulk-hit-${t.slug}`}
                >
                  <div>
                    <div className="text-sm">{t.name}</div>
                    <div className="text-xs text-slate-500 font-mono-nums">{t.slug}</div>
                  </div>
                  {active ? <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30">Bundle on</Badge> : <Badge className="bg-slate-700/40 text-slate-300">Off</Badge>}
                </button>
              );
            })}
          </div>
        )}
        {picked.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
            <div className="text-sm text-slate-300">{picked.length} selected: {picked.map((t) => t.slug).join(", ")}</div>
            <Button onClick={grant} disabled={granting} className="bg-fuchsia-600 hover:bg-fuchsia-500" data-testid="bulk-grant-btn">
              {granting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <LockOpen className="w-4 h-4 mr-2" />} Give 1 year (all addons)
            </Button>
          </div>
        )}
      </Card>

      <Card className="bg-slate-900 border-slate-800 p-6 space-y-3 text-slate-100" data-testid="bulk-purchasers">
        <div className="font-semibold">Stores with the buy-all bundle</div>
        {purchasers.length === 0 ? (
          <p className="text-sm text-slate-500">No store has purchased or been granted the all-addons bundle yet.</p>
        ) : (
          <div className="divide-y divide-slate-800">
            {purchasers.map((t) => {
              const active = Boolean(t.entitlement?.active);
              const exp = t.entitlement?.expires_at;
              return (
                <div key={t.id} className="py-3 flex flex-wrap items-center justify-between gap-2" data-testid={`bulk-buyer-${t.slug}`}>
                  <div>
                    <div className="text-sm font-medium">{t.name}</div>
                    <div className="text-xs text-slate-500 font-mono-nums">{t.slug} · {t.source || "—"}{t.amount != null ? ` · ₹${inr2(t.amount)}` : ""}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {active
                      ? <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 gap-1"><Check className="w-3 h-3" /> Active{exp ? ` until ${new Date(exp).toLocaleDateString("en-IN")}` : ""}</Badge>
                      : <Badge className="bg-slate-700/40 text-slate-300">{t.entitlement?.status || "inactive"}</Badge>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}
